/**
 * Session state.
 *
 * The access token lives in memory only. The refresh token is persisted to
 * IndexedDB, deliberately:
 *
 *   - `localStorage` is synchronous and shared with any injected script; Dexie
 *     at least keeps it out of the most casual XSS reach.
 *   - It must survive a cold launch, because an installed PWA opened in a
 *     basement with no signal has to reach a usable UI. We cannot re-authenticate
 *     offline, so the session has to already be there.
 *
 * The offline consequence is deliberate: an expired *access* token while offline
 * does not sign the user out. Local reads and writes continue against Dexie, and
 * the outbox re-authenticates when the network returns. Signing someone out
 * because a token aged out while they were in a store room would be a bug, not
 * security.
 */
import type { User } from '@mouldtrack/shared';
import { db, META_KEYS } from '../db/schema.js';

export interface Session {
  user: User;
  accessToken: string;
  refreshToken: string;
  /** When this device last proved the session against the server. */
  verifiedAt: string;
}

/** Persisted shape - the access token is intentionally not written to disk. */
type StoredSession = Omit<Session, 'accessToken'>;

type Listener = () => void;

let current: Session | null = null;
let hydrated = false;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

export const sessionStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  get(): Session | null {
    return current;
  },
  isHydrated(): boolean {
    return hydrated;
  },
};

/** Restore the session from IndexedDB on boot. Resolves before the first render. */
export async function hydrateSession(): Promise<Session | null> {
  if (hydrated) return current;
  try {
    const row = await db.meta.get(META_KEYS.session);
    const stored = row?.value as StoredSession | undefined;
    if (stored?.refreshToken && stored.user) {
      // No access token yet - the first authenticated call refreshes one. Offline,
      // there is nothing to refresh against and the UI runs on Dexie regardless.
      current = { ...stored, accessToken: '' };
    }
  } catch {
    // A corrupt or blocked IndexedDB should land the user on the login screen,
    // not on a white page.
    current = null;
  }
  hydrated = true;
  emit();
  return current;
}

export async function setSession(session: Session): Promise<void> {
  current = session;
  const { accessToken: _accessToken, ...stored } = session;
  await db.meta.put({ key: META_KEYS.session, value: stored satisfies StoredSession });
  emit();
}

/** Swap in a freshly minted access token without touching the persisted record. */
export function setAccessToken(accessToken: string): void {
  if (!current) return;
  current = { ...current, accessToken };
  emit();
}

export function updateUser(user: User): void {
  if (!current) return;
  current = { ...current, user, verifiedAt: new Date().toISOString() };
  void db.meta.put({
    key: META_KEYS.session,
    value: {
      user,
      refreshToken: current.refreshToken,
      verifiedAt: current.verifiedAt,
    } satisfies StoredSession,
  });
  emit();
}

/**
 * Sign out. Replicated data is wiped as well - these devices are shared between
 * shifts, and the next person must not inherit the last one's catalog.
 */
export async function clearSession(): Promise<void> {
  current = null;
  await db.meta.delete(META_KEYS.session);
  await db.clearReplicatedData();
  await db.meta.clear();
  emit();
}

export function getAccessToken(): string | null {
  return current?.accessToken || null;
}

export function getRefreshToken(): string | null {
  return current?.refreshToken || null;
}
